import { MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { Phone16Regular } from '@fluentui/react-icons';
import { QueryAggregate, type TableRecord } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  ExportGridButton,
  GridColumn,
  GridColumns,
  GridTemplateColumn,
  GridToolbarItems,
  Highlighter,
  HighlightFull,
  MainGrid,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
  matchesNumericSearch,
  parseNumericSearch,
  resolveColumnDisplayValue,
} from '@powerportalspro/react-fluent';
import { useMemo, type ReactNode } from 'react';
import {
  ALL_CONTACTS_VIEW_ID,
  MY_CONTACTS_VIEW_ID,
  getAllContactsView,
  getMyContactsView,
  useCurrentUserId,
} from './AccountAndContactCustomViews';

// The contacts, with each column's cells drawn by the page rather than the grid:
//
//   - Full Name       — a GridTemplateColumn composing firstname + lastname behind an initials
//                       avatar, linking to the contact's page.
//   - Email           — a mailto link with the contact's name in the recipient.
//   - Phone           — the formatted value behind a phone icon.
//   - Parent Company  — the lookup's name as plain text.
//   - Age             — a badge whose color (green, amber, red) the raw value picks.
//   - Birthday        — no cellRenderer: the built-in date cell, for comparison.
//
// A custom cell still takes part in everything else the grid does: `searchText` marks what the
// search matched, as the built-in cells do, and the totals row counts the emails and shows the
// earliest and latest birthday over every contact the grid matches.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.CustomCellRenderingMainDemo';

const CONTACT_URL = 'demos/contact';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalMNudge,
  },
  iconRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalSNudge,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
  },
  badge: {
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusXLarge,
    fontSize: tokens.fontSizeBase200,
  },
  danger: {
    backgroundColor: tokens.colorStatusDangerBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  warning: {
    backgroundColor: tokens.colorStatusWarningBackground3,
    color: tokens.colorNeutralForeground1,
  },
  success: {
    backgroundColor: tokens.colorStatusSuccessBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
  },
});

// The column's display text: Dataverse's formatted value when it has one, otherwise the
// raw value.
function readString(record: TableRecord, column: string): string {
  return resolveColumnDisplayValue(record, column) ?? '';
}

// The raw number behind a column, for threshold decisions.
function readNumber(record: TableRecord, column: string): number | null {
  const cell = record.properties?.[column];
  const value = cell && typeof cell === 'object' && 'value' in cell ? cell.value : undefined;
  return typeof value === 'number' ? value : null;
}

// A mailto link whose recipient carries the display name, e.g. "Jane Doe" <jane@...>.
function mailtoHref(email: string, displayName: string): string {
  return displayName
    ? `mailto:${email}?to=${encodeURIComponent(`"${displayName}" <${email}>`)}`
    : `mailto:${email}`;
}

// A number column's search is a comparison (`>40`): the whole value is marked when it
// holds, as the built-in number cells do.
function highlightNumber(value: number, text: string, searchText: string | null | undefined): ReactNode {
  const search = parseNumericSearch(searchText);
  return search && matchesNumericSearch(value, search) ? <HighlightFull text={text} /> : text;
}

export function CustomCellRenderingMainSample() {
  const styles = useStyles();
  const t = useT();
  const currentUserId = useCurrentUserId();

  const contactViews = useMemo(
    () => [getAllContactsView(), getMyContactsView(currentUserId)],
    [currentUserId],
  );

  return (
    <div className={styles.root}>
      <MessageBar intent="warning" layout="multiline">
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="550px"
        maxHeight="550px"
        pageSize={20}
        viewIds={[ALL_CONTACTS_VIEW_ID, MY_CONTACTS_VIEW_ID]}
        customViewDefinitions={contactViews}
        defaultViewId={ALL_CONTACTS_VIEW_ID}
        persistedStateQueryParameter="contactsGridState"
      >
        <GridColumns>
          {/* Full Name — composes firstname + lastname behind an initials avatar.
              dependsOn fetches the two names without showing them as columns; sortBy and
              searchBy point the header sort and the search at fullname, and searchText
              marks what the search matched. The name links to the contact's page. */}
          <GridTemplateColumn
            displayName={t(`${STRINGS_BASE}.col-full-name`)}
            width="220px"
            dependsOn={['firstname', 'lastname']}
            sortBy="fullname"
            searchBy={['fullname']}
            cellRenderer={({ record, searchText }) => {
              const first = readString(record, 'firstname');
              const last = readString(record, 'lastname');
              const initials = (first.charAt(0) + last.charAt(0)).toUpperCase();
              const name = (
                <strong>
                  <Highlighter text={`${first} ${last}`.trim() || '—'} searchText={searchText ?? null} />
                </strong>
              );
              return (
                <div className={styles.nameRow}>
                  <span className={styles.avatar}>{initials || '—'}</span>
                  {record.id ? <a href={`${CONTACT_URL}?contactId=${record.id}`}>{name}</a> : name}
                </div>
              );
            }}
          />

          {/* Email — a mailto link with the contact's name in the recipient. */}
          <GridColumn
            columnName="emailaddress1"
            displayName={t(`${STRINGS_BASE}.col-email`)}
            width="240px"
            aggregates={[{ aggregate: QueryAggregate.Count }]}
            cellRenderer={({ record, searchText }) => {
              const email = readString(record, 'emailaddress1');
              if (!email) return <span>—</span>;
              const display = `${readString(record, 'firstname')} ${readString(record, 'lastname')}`.trim();
              return (
                <a href={mailtoHref(email, display)}>
                  <Highlighter text={email} searchText={searchText ?? null} />
                </a>
              );
            }}
          />

          {/* Phone — the formatted value behind a phone icon. */}
          <GridColumn
            columnName="mobilephone"
            displayName={t(`${STRINGS_BASE}.col-phone`)}
            width="160px"
            cellRenderer={({ record, searchText }) => {
              const phone = readString(record, 'mobilephone');
              if (!phone) return <span>—</span>;
              return (
                <div className={styles.iconRow}>
                  <Phone16Regular />
                  <Highlighter text={phone} searchText={searchText ?? null} />
                </div>
              );
            }}
          />

          {/* Parent Company — the lookup's name as plain text. */}
          <GridColumn
            columnName="parentcustomerid"
            displayName={t(`${STRINGS_BASE}.col-parent-company`)}
            width="200px"
            cellRenderer={({ record, searchText }) => {
              const parent = readString(record, 'parentcustomerid');
              return parent ? <Highlighter text={parent} searchText={searchText ?? null} /> : <span>—</span>;
            }}
          />

          {/* Age — a badge whose color the raw value picks, showing the formatted value. */}
          <GridColumn
            columnName="ppp_age"
            displayName={t(`${STRINGS_BASE}.col-age`)}
            width="100px"
            align="center"
            cellRenderer={({ record, searchText }) => {
              const age = readNumber(record, 'ppp_age');
              if (age === null) return <span>—</span>;
              const color = age > 65 ? styles.danger : age > 40 ? styles.warning : styles.success;
              return (
                <span className={`${styles.badge} ${color}`}>
                  {highlightNumber(age, readString(record, 'ppp_age'), searchText)}
                </span>
              );
            }}
          />

          {/* Birthday — no cellRenderer, so the built-in date cell renders it, and
              highlights it for a date search such as >1990. */}
          <GridColumn
            columnName="birthdate"
            width="125px"
            aggregates={[{ aggregate: QueryAggregate.Minimum }, { aggregate: QueryAggregate.Maximum }]}
          />
        </GridColumns>
        <GridToolbarItems>
          <NavigateNewRecordGridButton url={CONTACT_URL} />
          <NavigateOpenRecordGridButton urlFor={(record) => `${CONTACT_URL}?contactId=${record.id}`} />
          <ExportGridButton />
        </GridToolbarItems>
      </MainGrid>
    </div>
  );
}
